import { useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { DESKTOP_BREAKPOINT } from "../../../../../../components/AppShell/styles";
import { Button } from "../../../../../../components/Button";
import ConfirmModal from "../../../../../../components/ConfirmModal";
import * as Fonts from "../../../../../../components/Fonts";
import { findDomainProvider } from "../../../../../../model/domainProviders";
import { CustomDomain } from "../../../../../../services/customDomain";
import { Section } from "../../styles";
import { DnsRecords } from "../DnsRecords";
import { ProviderIcon } from "../ProviderIcon";

/* Compact summary like the Keepo dashboard's first block: outlined panel, one row on desktop */
const Hero = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  align-items: center;
  padding: 1.25rem 1.5rem;
  border: 1px solid var(--color-neutral-dark);
  border-radius: 12px;

  @media not all and (min-width: ${DESKTOP_BREAKPOINT}) {
    padding: 20px;
  }

  .text {
    display: flex;
    flex: 1;
    flex-direction: column;
    gap: 0.25rem;
    min-width: 0;
  }

  .domain {
    overflow: hidden;
    font-family: Poppins, sans-serif;
    font-size: 1.25rem;
    font-weight: 700;
    line-height: 1.75rem;
    color: var(--color-neutral-white);
    text-overflow: ellipsis;
    white-space: nowrap;

    &:hover {
      text-decoration: underline;
    }
  }

  .meta {
    display: flex;
    gap: 0.5rem;
    align-items: center;
    font-family: Inter, sans-serif;
    font-size: 0.75rem;
    font-weight: 700;
    line-height: 1rem;
    color: var(--color-neutral-150);
  }

  .status {
    display: inline-flex;
    gap: 0.375rem;
    align-items: center;
    color: var(--color-feedback-success);

    &::before {
      width: 6px;
      height: 6px;
      content: "";
      background-color: currentColor;
      border-radius: 100%;
    }
  }

  /* The project's Button is a div: full width under the text on phones */
  .disconnect {
    flex-shrink: 0;

    @media not all and (min-width: ${DESKTOP_BREAKPOINT}) {
      width: 100%;
      text-align: center;
    }
  }
`;

type Props = {
  domain: CustomDomain;
  onDisconnect: () => Promise<void>;
};

/** Last step: the domain is live; it can be disconnected from here. */
export const ConnectedDomain = ({ domain, onDisconnect }: Props) => {
  const { t } = useTranslation();
  const [isConfirming, setConfirming] = useState(false);
  const [isDisconnecting, setDisconnecting] = useState(false);

  const prefix = "pages.settings.customDomain.connected";
  const provider = findDomainProvider(domain.provider);

  const disconnect = async () => {
    setDisconnecting(true);
    try {
      await onDisconnect();
    } finally {
      setDisconnecting(false);
      setConfirming(false);
    }
  };

  return (
    <>
      <Section>
        <Hero>
          <ProviderIcon provider={provider} size={48} />
          <div className="text">
            <a className="domain" href={`https://${domain.domain}`} target="_blank" rel="noreferrer">
              {domain.domain}
            </a>
            <span className="meta">
              <span className="status">{t(`${prefix}.status`)}</span>
              {provider && provider.key !== "other" && <span>{t(`${prefix}.via`, { provider: provider.name })}</span>}
            </span>
          </div>
          <Button type="OUTLINE" size="MD" className="disconnect" onClick={() => setConfirming(true)}>
            {t(`${prefix}.disconnect`)}
          </Button>
        </Hero>
      </Section>

      <Section>
        <Fonts.Label>{t("pages.settings.customDomain.records.section")}</Fonts.Label>
        <DnsRecords records={domain.records} />
      </Section>

      {isConfirming && (
        <ConfirmModal
          type="DANGER"
          title={t(`${prefix}.confirmModal.title`, { domain: domain.domain })}
          descriptions={[t(`${prefix}.confirmModal.description`)]}
          confirmButtonTitle={t(`${prefix}.confirmModal.confirm`)}
          cancelTitle={t(`${prefix}.confirmModal.cancel`)}
          confirming={isDisconnecting}
          onConfirm={disconnect}
          onCancel={() => setConfirming(false)}
        />
      )}
    </>
  );
};
